package org.matthewo.schema.games

import org.matthewo.api.MetricsIntoTheDeepAPI
import org.matthewo.math.div
import org.matthewo.math.minus
import org.matthewo.math.plus
import org.matthewo.schema.NonBonusRPRankingDetails
import org.matthewo.schema.StandardRankingMetrics
import org.matthewo.symbolprocessor.GameSchema
import org.matthewo.symbolprocessor.Metric
import org.matthewo.symbolprocessor.Period
import kotlin.math.E
import kotlin.math.abs
import kotlin.math.pow

@GameSchema(
    year = 2024,
    title = "Into The Deep",
    allianceSize = 2,
    hasRankingDetails = true,
)
interface MetricsIntoTheDeep<T : Number> : StandardRankingMetrics<T> {
    override val tbp1 get() = (apSampleNet + apSampleLow + apSampleHigh + apSpecimenLow + apSpecimenHigh) / 2 + apNavigation

    @Metric(Period.ALL, "Foul")
    override val penalty: T

    @Metric(Period.AUTO, "Sample Net", pointValue = 2.0 * SAMPLE_NET)
    val apSampleNet: T

    @Metric(Period.AUTO, "Sample Low", pointValue = 2.0 * SAMPLE_LOW)
    val apSampleLow: T

    @Metric(Period.AUTO, "Sample High", pointValue = 2.0 * SAMPLE_HIGH)
    val apSampleHigh: T

    @Metric(Period.AUTO, "Specimen Low", pointValue = 2.0 * SPECIMEN_LOW)
    val apSpecimenLow: T

    @Metric(Period.AUTO, "Specimen High", pointValue = 2.0 * SPECIMEN_HIGH)
    val apSpecimenHigh: T

    @Metric(Period.AUTO, "Navigation", pointValue = OBSERVATION_PARK.toDouble())
    val apNavigation: T

    @Metric(Period.TELEOP, "Sample Net", pointValue = SAMPLE_NET.toDouble())
    val tpSampleNet: T

    @Metric(Period.TELEOP, "Sample Low", pointValue = SAMPLE_LOW.toDouble())
    val tpSampleLow: T

    @Metric(Period.TELEOP, "Sample High", pointValue = SAMPLE_HIGH.toDouble())
    val tpSampleHigh: T

    @Metric(Period.TELEOP, "Specimen Low", pointValue = SPECIMEN_LOW.toDouble())
    val tpSpecimenLow: T

    @Metric(Period.TELEOP, "Specimen High", pointValue = SPECIMEN_HIGH.toDouble())
    val tpSpecimenHigh: T

    @Metric(Period.ENDGAME, "Navigation")
    val epNavigation: T

    companion object {
        const val SAMPLE_NET = 2
        const val SAMPLE_LOW = 4
        const val SAMPLE_HIGH = 8
        const val SPECIMEN_LOW = 6
        const val SPECIMEN_HIGH = 10
        const val OBSERVATION_PARK = 3
        const val ASCENT_1 = 3
        const val ASCENT_2 = 15
        const val ASCENT_3 = 30
    }
}

val <T : Number> MetricsIntoTheDeep<T>.sample: T
    get() = apSampleNet + apSampleLow + apSampleHigh +
            tpSampleNet + tpSampleLow + tpSampleHigh

val <T : Number> MetricsIntoTheDeep<T>.specimen: T
    get() = apSpecimenLow + apSpecimenHigh +
            tpSpecimenLow + tpSpecimenHigh

val <T : Number> MetricsIntoTheDeep<T>.navigation: T
    get() = apNavigation + epNavigation

fun <T : Number> MutableMetricsIntoTheDeep<T>.computeTotals() {
    auto = apSampleNet + apSampleLow + apSampleHigh + apSpecimenLow + apSpecimenHigh + apNavigation
    teleop = tpSampleNet + tpSampleLow + tpSampleHigh + tpSpecimenLow + tpSpecimenHigh
    endgame = epNavigation
    npTotal = auto + teleop + endgame
    total = npTotal - penalty
}

object IntoTheDeepRankingDetails : NonBonusRPRankingDetails<MetricsIntoTheDeep<*>>() {
    override fun combine(team1: MetricsIntoTheDeep<*>, team2: MetricsIntoTheDeep<*>) = MetricsIntoTheDeepAPI<Double>(team1, team2)

    override fun winProbability(alliance: MetricsIntoTheDeep<*>?, opponent: MetricsIntoTheDeep<*>?, playedFactor: Double): Double {
        if (null == alliance || null == opponent) return 0.5
        val diff = alliance.npTotal.toDouble() - opponent.npTotal.toDouble()
        val percent = -0.5 * E.pow(-0.025 * abs(diff)) + 1.0
        return if (diff >= 0) percent else 1.0 - percent
    }
}
